import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen, Clock, User, Loader2, Search, Filter,
  GraduationCap, Layers, Award, ChevronRight, Star, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "All Categories",
  "AI & Technology", "Public Health", "Pharmacology", "Medical Ethics",
  "Digital Health", "Research Methods", "Psychiatry", "Healthcare Management",
  "Genetics & Genomics", "Clinical Skills", "Surgery", "Pediatrics",
  "Data Science", "Academic Writing", "Emergency Medicine"
];

const LEVELS = ["All Levels", "Beginner", "Intermediate", "Advanced"];

export default function CoursesPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [level, setLevel] = useState('All Levels');
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  useEffect(() => { fetchCourses(); fetchEnrollments(); }, [category, level, search]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'All Categories') params.append('category', category);
      if (level && level !== 'All Levels') params.append('level', level);
      if (search) params.append('search', search);

      const res = await axios.get(`${API}/courses?${params.toString()}`);
      setCourses(res.data.courses || []);
    } catch { toast.error('Failed to load courses'); }
    finally { setLoading(false); }
  };

  const fetchEnrollments = async () => {
    if (!user) return;
    try {
      const res = await api.get('/my-courses');
      setEnrolledCourses(res.data.courses || []);
    } catch { }
  };

  const handleWaitlist = async (course) => {
    if (!user) {
      toast.error('Please log in to join the waitlist');
      navigate('/auth');
      return;
    }
    try {
      const res = await api.post(`/courses/${course.course_id}/waitlist`);
      toast.success(res.data?.message || "You've been added to the waitlist");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join waitlist');
    }
  };

  const handleEnroll = async (course) => {
    if (!user) {
      toast.error('Please log in to enroll');
      navigate('/auth');
      return;
    }

    if (isEnrolled(course.course_id)) {
      navigate(`/courses/${course.course_id}`);
      return;
    }

    try {
      if (course.price > 0) {
        const res = await api.post('/payments/create-checkout', {
          course_id: course.course_id,
          origin_url: window.location.origin
        });
        if (res.data.url) window.location.href = res.data.url;
      } else {
        await api.post(`/courses/${course.course_id}/enroll`);
        toast.success('Successfully enrolled!');
        setEnrolledCourses(prev => [...prev, { course_id: course.course_id, completed: false, progress_percent: 0 }]);
        navigate(`/courses/${course.course_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enroll');
    }
  };

  const getEnrollment = (courseId) => enrolledCourses.find((c) => {
    if (typeof c === 'string') return c === courseId;
    return c?.course_id === courseId;
  });
  const isEnrolled = (courseId) => !!getEnrollment(courseId);
  const isCourseCompleted = (courseId) => {
    const enrollment = getEnrollment(courseId);
    if (!enrollment || typeof enrollment === 'string') return false;
    return Boolean(enrollment.completed) || Number(enrollment.progress_percent || 0) >= 100;
  };

  return (
    <div className="page-container min-h-screen bg-slate-50" data-testid="courses-page">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-[#0a2942] to-[#1a5c5c] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="max-w-2xl">
            <Badge className="bg-white/20 text-white border-0 mb-4">Course Catalog</Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>
              Master Medical Excellence
            </h1>
            <p className="text-base text-white/80 mb-6">
              Upskill across the medical ecosystem — from AI to public health, ethics to leadership.
              Courses created by top institutions and verified experts.
            </p>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search courses by title or topic..."
                className="w-full pl-12 h-12 bg-white text-slate-900 border-0 rounded-xl shadow-lg"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Filter className="h-4 w-4" />
            <span>Filter by:</span>
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full sm:w-56" data-testid="category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-full sm:w-40" data-testid="level-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500 ml-auto hidden sm:block">
            {courses.length} course{courses.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Course Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 text-[#1a5c5c] animate-spin" />
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
            <BookOpen className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No courses found</h3>
            <p className="text-slate-500 mb-4">Try adjusting your filters or search terms</p>
            <Button variant="outline" onClick={() => { setSearch(''); setCategory('All Categories'); setLevel('All Levels'); }}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div
                key={course.course_id}
                className="group bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`course-card-${course.course_id}`}
              >
                {/* Course Image/Placeholder */}
                <div className="h-40 bg-gradient-to-br from-[#1a5c5c]/10 to-[#0a2942]/10 relative overflow-hidden">
                  {course.image_url ? (
                    <img
                      src={course.image_url}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-[#1a5c5c]/30" />
                    </div>
                  )}

                  {/* Enrolled Badge */}
                  {isEnrolled(course.course_id) && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-emerald-500 text-white border-0">
                        <GraduationCap className="h-3 w-3 mr-1" /> Enrolled
                      </Badge>
                    </div>
                  )}

                  {/* Price Tag */}
                  <div className="absolute bottom-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${course.price > 0
                        ? 'bg-white text-[#0a2942] shadow-lg'
                        : 'bg-emerald-500 text-white'
                      }`}>
                      {course.price > 0 ? `$${course.price}` : 'Free'}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  {/* Category & Level */}
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-[#1a5c5c]/10 text-[#1a5c5c] border-0 text-xs">{course.category}</Badge>
                    <Badge variant="outline" className="text-xs text-slate-500">{course.level}</Badge>
                  </div>

                  {/* Title */}
                  <h3
                    className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 min-h-[3.5rem] group-hover:text-[#1a5c5c] transition-colors cursor-pointer"
                    style={{ fontFamily: 'Manrope' }}
                    onClick={() => navigate(`/courses/${course.course_id}`)}
                  >
                    {course.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4">{course.short_description || course.description}</p>

                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                    {course.duration && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {course.duration}
                      </span>
                    )}
                    {course.modules_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Layers className="h-3.5 w-3.5" />
                        {course.modules_count} modules
                      </span>
                    )}
                    {course.certification && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <Award className="h-3.5 w-3.5" />
                        Certificate
                      </span>
                    )}
                  </div>

                  {/* Instructor & University */}
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-4 pb-4 border-b border-slate-100">
                    <User className="h-4 w-4 text-slate-400" />
                    <span className="truncate">{course.instructor || 'Instructor TBD'}</span>
                    {course.university_name && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span className="text-xs text-slate-500 truncate">{course.university_name}</span>
                      </>
                    )}
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={() => {
                      if (course.status === 'waitlisted') {
                        handleWaitlist(course);
                        return;
                      }
                      if (isEnrolled(course.course_id)) {
                        navigate(`/courses/${course.course_id}`);
                        return;
                      }
                      handleEnroll(course);
                    }}
                    className={`w-full ${isEnrolled(course.course_id)
                        ? isCourseCompleted(course.course_id)
                          ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        : course.status === 'waitlisted'
                          ? 'bg-[#0a2942] hover:bg-[#0d3a5c] text-white'
                          : 'bg-[#1a5c5c] hover:bg-[#154a4a] text-white'
                      } transition-colors`}
                    data-testid={`enroll-btn-${course.course_id}`}
                  >
                    {isEnrolled(course.course_id) ? (
                      isCourseCompleted(course.course_id) ? (
                        <>Completed <CheckCircle className="h-4 w-4 ml-1" /></>
                      ) : (
                        <>Continue Learning <ChevronRight className="h-4 w-4 ml-1" /></>
                      )
                    ) : course.status === 'waitlisted' ? (
                      'Waitlist'
                    ) : course.price > 0 ? (
                      'Enroll Now'
                    ) : (
                      'Start Free Course'
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
